import { useQuery } from '@tanstack/react-query'
import type { Patient } from '../types/patient'
import { vitalSignsService } from '../services/vitalSignsService'
import {
  formatPatientBlockForLetter,
  type LetterBlockType,
} from '../utils/letterBlockFormatters'

interface LetterInsertToolbarProps {
  patient: Patient | null | undefined
  patientId: string | null
  onInsert: (text: string) => void
  disabled?: boolean
}

const BLOCK_CONFIG: Array<{
  type: LetterBlockType
  label: string
  icon: React.ReactNode
}> = [
  {
    type: 'patient-details',
    label: 'Insert patient details',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 2-2 2H4c-1 0-2-1-2-2v-1a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1zm0-5v-1a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v1h12z" />
      </svg>
    ),
  },
  {
    type: 'allergies',
    label: 'Insert allergies',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 1a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V6a.5.5 0 0 1-1 0V4H6a.5.5 0 0 1 0-1h1.5V1.5A.5.5 0 0 1 8 1z" />
        <path d="M4.5 8a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5z" />
      </svg>
    ),
  },
  {
    type: 'current-medications',
    label: 'Insert current medications',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M4 2a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v1h1a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h1V2zm2 0v1h4V2H6zM3 4v10h10V4H3z" />
      </svg>
    ),
  },
  {
    type: 'address',
    label: 'Insert address',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 0a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 0 1h-.5v.5a.5.5 0 0 1-1 0V2h-.5a.5.5 0 0 1 0-1h.5V.5A.5.5 0 0 1 8 0z" />
        <path d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 1a3 3 0 1 1 0 6 3 3 0 0 1 0-6z" />
        <path d="M8 12a.5.5 0 0 1 .5-.5h.5v-1a.5.5 0 0 1 1 0v1h.5a.5.5 0 0 1 0 1H10v1a.5.5 0 0 1-1 0v-1h-.5A.5.5 0 0 1 8 12z" />
      </svg>
    ),
  },
  {
    type: 'contact',
    label: 'Insert contact details',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z" />
      </svg>
    ),
  },
  {
    type: 'chronic-conditions',
    label: 'Insert chronic conditions',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H4z" />
        <path d="M4.5 4a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1h-7zm0 2a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1h-7zm0 2a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1h-4z" />
      </svg>
    ),
  },
  {
    type: 'medical-history',
    label: 'Insert medical history',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z" />
      </svg>
    ),
  },
  {
    type: 'latest-vitals',
    label: 'Insert latest vitals',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 2a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 0 1h-.5v.5a.5.5 0 0 1-1 0V4h-.5a.5.5 0 0 1 0-1h.5V2.5A.5.5 0 0 1 8 2z" />
        <path d="M4 8a.5.5 0 0 1 .5-.5h.5v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-.5v.5a.5.5 0 0 1-1 0V8.5H4.5A.5.5 0 0 1 4 8zm8 0a.5.5 0 0 1 .5-.5h.5v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-.5v.5a.5.5 0 0 1-1 0V8.5h-.5A.5.5 0 0 1 12 8z" />
        <path d="M8 12a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 0 1h-.5v.5a.5.5 0 0 1-1 0v-.5h-.5a.5.5 0 0 1 0-1h.5v-.5A.5.5 0 0 1 8 12z" />
      </svg>
    ),
  },
]

export function LetterInsertToolbar({
  patient,
  patientId,
  onInsert,
  disabled = false,
}: LetterInsertToolbarProps) {
  const { data: vitals = [] } = useQuery({
    queryKey: ['vital-signs', 'patient', patientId],
    queryFn: () => vitalSignsService.getByPatient(patientId!),
    enabled: !!patientId && !disabled,
  })

  if (!patient) return null

  const handleClick = (type: LetterBlockType) => {
    const text = formatPatientBlockForLetter(
      patient,
      type,
      type === 'latest-vitals' ? vitals : undefined
    )
    if (text) onInsert(text)
  }

  return (
    <div className="letter-insert-toolbar" role="toolbar" aria-label="Insert patient information">
      {BLOCK_CONFIG.map(({ type, label, icon }) => {
        const isVitals = type === 'latest-vitals'
        const hasVitalsData = isVitals && vitals && vitals.length > 0
        const isDisabled = disabled || (isVitals && !hasVitalsData)

        return (
          <button
            key={type}
            type="button"
            className="letter-insert-btn letter-insert-btn-tooltip"
            onClick={() => handleClick(type)}
            disabled={isDisabled}
            title={label}
            aria-label={label}
            data-tooltip={label}
          >
            {icon}
          </button>
        )
      })}
    </div>
  )
}
